import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {useFormik, getIn} from "formik";
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Button, MenuItem, Skeleton, Step, StepLabel, Stepper, Stack,
    TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {AltRoute} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import BetaBadge from "./layout/BetaBadge";
import SubmitButton from "./layout/forms/SubmitButton";
import Constants from "../../utils/constants";
import {toastHelper} from "../../utils/toast-helper";
import './UsersNewOrEdit.css';
import {
    useCreateSimulationMutation, useGetAgencesSimulationQuery, useGetPatrimoinesSimulationQuery,
    useGetSimulationQuery,
} from "../../modules/simulations/simulations.api";
import {
    coutKmValide, etapeDuChamp, etapeMinimale, appliquerCalendrier, parseFiger, PERIODE_MAX_JOURS, peutLancer,
    toggleContraint, construirePayload, valeursInitiales, valeursOuverture, soumettreScenario,
} from "./simulations/simulationRules";
import {useEtatCalendrier} from "./simulations/useEtatCalendrier";
import {mentionRecap} from "./simulations/calibrationRules";
import {rejouerDe} from "./simulations/echangeScenarioRules";
import {schema} from "./simulations/simulationSchema";
import {
    ChampsAvances, ChampsBudgets, ChampsCollecte, ChampsCouts, ChampsDepart, ChampsEquipes, ChampsPeriode, ChampsVerrous,
} from "./simulations/SimulationWizardFields";

const ETAPES = ['Période et équipes', 'Collecte et budgets', 'Coûts et lancement'];

function Section({titre, children}) {
    return (
        <div className="sectionContainer">
            <Typography variant="h5" className="sectionHeader">{titre}</Typography>
            {children}
        </div>
    );
}

function Etape1({formik, tentee, agences, agencesLoading}) {
    return (
        <>
            <Section titre="Période et équipes">
                <ChampsPeriode formik={formik} tentee={tentee}/>
                <ChampsEquipes formik={formik} tentee={tentee}/>
            </Section>
            <Section titre="Point de départ">
                <ChampsDepart formik={formik} tentee={tentee} agences={agences} agencesLoading={agencesLoading}/>
            </Section>
        </>
    );
}

function Etape2({formik, tentee, patrimoine, aCalendrier, calendrierLoading, calendrierErreur}) {
    return (
        <>
            <Section titre="Collecte">
                <ChampsCollecte formik={formik} tentee={tentee} aCalendrier={aCalendrier}
                                calendrierLoading={calendrierLoading} calendrierErreur={calendrierErreur}/>
            </Section>
            <Section titre="Budgets"><ChampsBudgets formik={formik} tentee={tentee}/></Section>
            <Section titre="Verrous"><ChampsVerrous formik={formik} tentee={tentee}/></Section>
            <Accordion disableGutters variant="outlined" sx={{mt: 2}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant="h5" className="sectionHeader" sx={{mb: 0}}>Réglages avancés</Typography>
                </AccordionSummary>
                <AccordionDetails><ChampsAvances formik={formik} tentee={tentee} patrimoine={patrimoine}/></AccordionDetails>
            </Accordion>
        </>
    );
}

function libelleBase(p, agences) {
    if (!p) return 'aucun';
    if (p.mode === 'agence') return (agences || []).find((a) => String(a.id) === String(p.agence))?.libelle ?? `agence ${p.agence ?? '?'}`;
    return `${p.adresse || 'adresse libre'}${p.lat != null ? ` (${Number(p.lat).toFixed(4)}, ${Number(p.lon).toFixed(4)})` : ''}`;
}

function Etape3({formik, tentee, agences}) {
    const v = formik.values;
    return (
        <>
            <Section titre="Coûts"><ChampsCouts formik={formik} tentee={tentee}/></Section>
            <Section titre="Récapitulatif">
                <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{v.libelle || '—'}</strong></Typography>
                    <Typography variant="body2">Du {v.du || '—'} au {v.au || '—'} · {v.nbEquipes} équipe(s) · équipage {v.equipage === 'auto' ? 'auto' : v.equipage === 2 ? 'binôme' : 'seul'}</Typography>
                    <Typography variant="body2">Base : {libelleBase(v.base, agences)}{v.depot ? ` · dépôt : ${libelleBase(v.depot, agences)}` : ''} · Déplacement : {v.deplacement ? 'oui' : 'non'}</Typography>
                    <Typography variant="body2">{v.contraint ? 'Contraint à la collecte' : 'Mode libre'} · {v.heuresParJour} h/jour · {v.joursParSemaine} j/semaine</Typography>
                    <Typography variant="body2">{v.interdits.length} date(s) interdite(s) · {v.jours.length} PAV figé(s){v.horizonVerrouille ? ' · horizon verrouillé' : ''}{mentionRecap(v) ? ` · ${mentionRecap(v)}` : ''}</Typography>
                </Stack>
            </Section>
            <Typography variant="caption" sx={{display: 'block', mt: 2}}>Simulation : ne crée rien dans l'ERP.</Typography>
        </>
    );
}

function SimulationNewPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const patrimoineId = searchParams.get('patrimoine');
    const fromId = searchParams.get('from');
    const figer = useMemo(() => parseFiger(searchParams.get('figer')), [searchParams]);

    const [etape, setEtape] = useState(0);
    const [tentees, setTentees] = useState({});
    const [erreursGlobales, setErreursGlobales] = useState([]);

    const {data: patrimoines = []} = useGetPatrimoinesSimulationQuery();
    const patrimoine = patrimoines.find((p) => String(p.id) === String(patrimoineId));
    const {data: agences = [], isLoading: agencesLoading} = useGetAgencesSimulationQuery();
    const {data: source, isLoading: sourceLoading, error: sourceError} = useGetSimulationQuery(fromId, {skip: !fromId});

    const {loading: calendrierLoading, erreur: calendrierErreur, present: aCalendrier} = useEtatCalendrier(patrimoineId);

    const [createSimulation, {isLoading: envoi}] = useCreateSimulationMutation();

    const formik = useFormik({
        initialValues: valeursInitiales(),
        validationSchema: schema,
        validateOnMount: false,
        onSubmit: async (values) => {
            setErreursGlobales([]);
            try {
                const r = await soumettreScenario((payload) => createSimulation(payload).unwrap(), values, patrimoineId,
                    (v, p) => construirePayload(v, p, rejouerDe(source)));
                if (r.cree) {
                    toastHelper.displaySuccess("Simulation lancée : le calcul prend quelques secondes.");
                    navigate(`${Constants.APP_ROUTE_SIMULATIONS}?patrimoine=${patrimoineId}`);
                    return;
                }
                formik.setErrors(r.erreurs);
                setErreursGlobales(r.globales);
                setTentees({0: true, 1: true, 2: true});
                const premiere = etapeMinimale(r.erreurs);
                if (premiere !== null) setEtape(premiere);
            } catch (e) {
                toastHelper.handleError(e, "Oops, une erreur est survenue.");
            }
        },
    });

    // Pré-remplissage : `?from=<id>` (Dupliquer / Figer ce jour), sinon vierge ; le calendrier fixe le défaut de « contraint ».
    useEffect(() => {
        // Source inconnue (404) → comme « pas de source » ; on attend source et calendrier avant de poser les valeurs.
        if ((fromId && !source && !sourceError) || calendrierLoading) return;
        const valeurs = valeursOuverture(fromId && source ? source : null, figer);
        const base = fromId && source ? valeurs : {...valeurs, contraint: aCalendrier};
        formik.resetForm({values: appliquerCalendrier(base, aCalendrier)});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, sourceError, fromId, figer, calendrierLoading, aCalendrier]);

    const tentee = Boolean(tentees[etape]);

    const suivant = async () => {
        const errs = await formik.validateForm();
        const bloquantes = Object.keys(errs).filter((k) => etapeDuChamp(k) === etape);
        setTentees((t) => ({...t, [etape]: true}));
        if (bloquantes.length === 0) {
            setEtape((e) => e + 1);
        }
    };

    const lancer = async (e) => {
        e.preventDefault();
        setTentees((t) => ({...t, [etape]: true}));
        formik.handleSubmit();
    };

    const retour = `${Constants.APP_ROUTE_SIMULATIONS}${patrimoineId ? `?patrimoine=${patrimoineId}` : ''}`;

    if (!patrimoineId) {
        return (
            <>
                <PageTitle title="Nouveau scénario" icon={<AltRoute/>}/>
                <Alert severity="info">Choisissez d'abord un patrimoine depuis la liste des simulations.</Alert>
                <Button color="primary" onClick={() => navigate(Constants.APP_ROUTE_SIMULATIONS)}>Retour aux simulations</Button>
            </>
        );
    }

    return (
        <>
            <Stack direction="row" alignItems="center">
                <PageTitle title="Nouveau scénario" icon={<AltRoute/>}/>
                <BetaBadge/>
            </Stack>
            <Typography variant="body2" sx={{mb: 2}}>
                Patrimoine : <strong>{patrimoine?.libelle ?? `n° ${patrimoineId}`}</strong>
                {fromId && source && <> · dérivé du scénario « {source.libelle} » (un scénario ne se modifie jamais : celui-ci en crée un nouveau)</>}
            </Typography>

            {sourceError && <Alert severity="error" sx={{mb: 2}}>Scénario source introuvable : le formulaire est vierge.</Alert>}

            <Stepper activeStep={etape} sx={{mb: 2}}>
                {ETAPES.map((libelle) => <Step key={libelle}><StepLabel>{libelle}</StepLabel></Step>)}
            </Stepper>

            {fromId && sourceLoading
                ? <Stack spacing={1}><Skeleton variant="rectangular" height={120}/><Skeleton variant="rectangular" height={200}/></Stack>
                : <form onSubmit={lancer} noValidate>
                    {erreursGlobales.length > 0 &&
                        <Alert severity="error" sx={{mb: 2}}>
                            {erreursGlobales.map((m, i) => <div key={i}>{m}</div>)}
                        </Alert>}
                    {etape === 0 && <Etape1 formik={formik} tentee={tentee} agences={agences} agencesLoading={agencesLoading}/>}
                    {etape === 1 && <Etape2 formik={formik} tentee={tentee} patrimoine={patrimoineId} aCalendrier={aCalendrier} calendrierLoading={calendrierLoading} calendrierErreur={calendrierErreur}/>}
                    {etape === 2 && <Etape3 formik={formik} tentee={tentee} agences={agences}/>}

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mt: 3}}>
                        <Button color="primary" onClick={() => (etape === 0 ? navigate(retour) : setEtape(etape - 1))}>
                            {etape === 0 ? 'Annuler' : 'Précédent'}
                        </Button>
                        {etape < 2
                            ? <Button color="primary" variant="contained" onClick={suivant}>Suivant</Button>
                            : <div style={{minWidth: 260}}>
                                <SubmitButton label="Lancer la simulation" submitting={envoi}
                                              disabled={!peutLancer(formik.values)}/>
                            </div>}
                    </Stack>
                    {etape === 2 && !coutKmValide(formik.values.coutKm) &&
                        <Typography variant="caption" sx={{display: 'block', textAlign: 'right'}}>
                            Renseignez le coût au km pour lancer la simulation.
                        </Typography>}
                </form>}
        </>
    );
}

export default SimulationNewPage;
